/**
 * Generate themes end to end for any entry point: a single shared Dialog whose
 * content swaps between the suggestion list and the review form (no modal-on-modal)
 * or one-click "Add".
 */
import { useState } from "react";
import { Dialog } from "@mui/material";
import { useSelector } from "react-redux";

import { notifyError, notifySuccess } from "../../../../store/actions/notify";
import { createTheme, useQceDispatch, useThemes } from "../../../../store/state/qce/qceSlice";
import { qceErrMsg } from "../../../../store/state/qce/qceService";
import type { RootState } from "../../../../store/state/rootReducer";

import GenerateThemesListContent from "./GenerateThemesModal";
import { CT_COLORS } from "./icons";
import { ThemeModalBody } from "./ThemeModal";
import { markSuggestionSaved, suggestionsStorageKey } from "./themeSuggestionsStore";
import type { ThemeDraft, ThemeWrite } from "./types";

interface GenerateThemesFlowProps {
  orgId: string;
  projectId: string;
  /** Generate from these recordings only (omit = the whole project). */
  storyIds?: string[];
  /** e.g. "In this recording" / "In 3 selected recordings". */
  scopeLabel?: string;
  onClose: () => void;
}

export function GenerateThemesFlow({ orgId, projectId, storyIds, scopeLabel, onClose }: GenerateThemesFlowProps) {
  const dispatch = useQceDispatch();
  const { themes, refetch } = useThemes(projectId);
  const [draft, setDraft] = useState<ThemeDraft | null>(null);
  const userId = useSelector((s: RootState) => s.auth?.user_id) as string | undefined;
  const storageKey = suggestionsStorageKey(userId || "anonymous", projectId, storyIds);

  const toast = (msg: string, type: "success" | "error" = "success") =>
    dispatch(type === "success" ? notifySuccess(msg) : notifyError(msg));

  const quickAdd = async (d: ThemeDraft) => {
    const body: ThemeWrite = {
      name: d.name,
      description: d.description,
      keywords: d.keywords,
      icon: d.icon || "house",
      color: d.color || CT_COLORS[themes.length % CT_COLORS.length].icon,
      exemplars: d.exemplars || [],
      skill: d.skill || "",
      from_skill: false,
      notes: "",
      links: [],
      projectId,
    };
    try {
      await dispatch(createTheme(body)).unwrap();
      toast(`Created "${d.name}"`);
    } catch (e: any) {
      toast(qceErrMsg(e, "Could not create theme"), "error");
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px", maxWidth: "640px" } }}
    >
      {draft ? (
        <ThemeModalBody
          draft={draft}
          existingCount={themes.length}
          projectId={projectId}
          onBack={() => setDraft(null)}
          onClose={() => setDraft(null)}
          onSaved={() => {
            // draft.name is the generated name; the review may have renamed the theme.
            markSuggestionSaved(storageKey, draft.name);
            setDraft(null);
            refetch();
            toast("Theme created");
          }}
          toast={toast}
        />
      ) : (
        <GenerateThemesListContent
          orgId={orgId}
          projectId={projectId}
          storyIds={storyIds}
          scopeLabel={scopeLabel}
          onPick={setDraft}
          onQuickAdd={quickAdd}
          onClose={onClose}
        />
      )}
    </Dialog>
  );
}
